// The client's view of a room (docs/ARCHITECTURE.md §1, §6, §7, §15). A Match talks to any room through a
// MatchChannel: commands in; views, private state and events out; the loss; release. What differs is a capability on
// the channel, never a check of which room it is: the local Worker room can pause (single-player, D10), an online
// war room reports presence (who is connected, its countdowns) and its link (live, or reconnecting after a drop). Both
// speak the same rpc messages over any WebSocketAdapter, so the local room's Worker socket and a real WebSocket differ
// only in the factory.
//
// Reconnect (online rooms, docs/ARCHITECTURE.md §15): once joined, a dropped socket — closed, or silent for
// SILENCE_LIMIT_MS — is replaced by a new one to the same room URL (its seat claim lives as long as the room) and the
// seat joins again, until the room's reconnect grace runs out. The room answers a rejoin with where commands resume
// (OnlineJoin.lastSeq) and a full view frame, announced to the Match by onResync first. A room close that rejoining
// cannot undo (FINAL_CLOSES: the war is over, another tab took the seat, the claim is refused) ends the link at once.
// A page that closes is not brought back: the room surrenders for its seat after the grace.

import { RpcClient, RpcError, type WebSocketAdapter, type WebSocketFactory } from '@esengine/rpc'
import { ONLINE_MATCH } from '@taxwar/content'
import {
  InvariantViolation,
  createProtocolCodec,
  onlineWarProtocol,
  taxwarProtocol,
  type Command,
  type MatchSetupView,
  type OnlineJoin,
  type OnlineWarProtocol,
  type PlayerPrivateState,
  type RoomFault,
  type RoomPresence,
  type SeatCommand,
  type SimEvent,
  type SkirmishSetup,
  type TaxwarProtocol,
  type ViewFrame,
} from '@taxwar/net'
import { CloseCode } from '@taxwar/online'
import { SocketErrorScope, type SocketClose, type SocketObserver } from '../online/browser-socket'
import { RoomFaultError, TransportError } from './errors'

export type Unsubscribe = () => void

/** Single-player pause (D10): the local room only. Both are idempotent. */
export interface PauseControl {
  pause(): Promise<void>
  resume(): Promise<void>
}

/** Online rooms: who is connected and the room's countdowns. A late subscriber gets the latest report at once. */
export interface PresenceFeed {
  onPresence(listener: (presence: RoomPresence) => void): Unsubscribe
}

/** An online war's link to its room. */
export type LinkState =
  /** Joined: commands go through. */
  | { readonly kind: 'live' }
  /** The connection dropped: the channel keeps rejoining until `giveUpAtMs` (epoch ms), then the war is lost. */
  | { readonly kind: 'reconnecting'; readonly giveUpAtMs: number }

/** Online rooms: the link's state, and the resync that follows every rejoin. */
export interface LinkFeed {
  /** A late subscriber gets the current state at once (once the seat has joined). */
  onLink(listener: (state: LinkState) => void): Unsubscribe
  /**
   * The seat rejoined its war: everything held from the room is stale, and the next view frame is a full one (spawns
   * of everything in sight). Delivered before that frame.
   */
  onResync(listener: () => void): Unsubscribe
}

/**
 * What a Match needs from its room. Subscribe to view/private/events before the war starts: the first view frame
 * carries the spawns every later delta frame builds on.
 */
export interface MatchChannel {
  /** Sends a command and returns its seq, which a later CommandRejected event refers to. */
  sendCommand(command: Command): number
  onView(listener: (frame: ViewFrame) => void): Unsubscribe
  onPrivate(listener: (state: PlayerPrivateState) => void): Unsubscribe
  onEvents(listener: (events: readonly SimEvent[]) => void): Unsubscribe
  /**
   * The room closed without dispose(): the match ends in a "connection lost" state. Subscribing after the loss
   * delivers it at once, so a loss can never go unseen. A room that reports its own fault is not "lost": the
   * connection raises a RoomFaultError through reportError instead (the app's crash report, §13).
   */
  onLost(listener: (error: TransportError) => void): Unsubscribe
  /** Closes the connection and releases the room (the local room's Worker is terminated). */
  dispose(): void
  readonly pause: PauseControl | null
  readonly presence: PresenceFeed | null
  readonly link: LinkFeed | null
}

/** The local (single-player) room: the client creates the war, and may pause it. */
export interface LocalRoomConnection extends MatchChannel {
  startSkirmish(setup: SkirmishSetup): Promise<MatchSetupView>
  readonly pause: PauseControl
  readonly presence: null
  readonly link: null
}

/** An online war room: the matchmaker created the war; this seat joins it. */
export interface OnlineRoomConnection extends MatchChannel {
  /**
   * Seats this connection and answers with the war; commands continue after the seq the room last saw from this seat.
   * Called once: after it, the channel rejoins by itself whenever the link drops (LinkFeed).
   */
  join(): Promise<OnlineJoin>
  readonly pause: null
  readonly presence: PresenceFeed
  readonly link: LinkFeed
}

/** The room ignores seq <= the last one it saw from this seat, which starts at 0. */
const FIRST_COMMAND_SEQ = 1

/** Copy-on-write listener list (room and Match channels): emitting (every tick) iterates a stable array without allocating. */
export class Listeners<T> {
  private listeners: readonly ((value: T) => void)[] = []

  add(listener: (value: T) => void): Unsubscribe {
    this.listeners = [...this.listeners, listener]
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener)
    }
  }

  clear(): void {
    this.listeners = []
  }

  readonly emit = (value: T): void => {
    const listeners = this.listeners
    for (let i = 0; i < listeners.length; i++) (listeners[i] as (value: T) => void)(value)
  }
}

/**
 * The part of an rpc client the shared channel drives. Each connection wires its own client's messages to the
 * channel (wireMessages): the rpc client's generic on() cannot be typed against one shared message set.
 */
interface CommandRpc {
  readonly isConnected: boolean
  connect(): Promise<unknown>
  disconnect(): void
  send(name: 'command', data: SeatCommand): void
}

type ConnectionState =
  | { readonly kind: 'open' }
  | { readonly kind: 'lost'; readonly error: TransportError }
  | { readonly kind: 'faulted' }
  | { readonly kind: 'disposed' }

/** The shared half of every room connection: listeners, the loss/fault state machine, the command seq. */
abstract class RpcRoomChannel {
  private state: ConnectionState = { kind: 'open' }
  private nextSeq = FIRST_COMMAND_SEQ
  protected readonly views = new Listeners<ViewFrame>()
  protected readonly privates = new Listeners<PlayerPrivateState>()
  protected readonly events = new Listeners<readonly SimEvent[]>()
  private readonly lost = new Listeners<TransportError>()
  protected abstract readonly client: CommandRpc

  async connect(): Promise<void> {
    await this.client.connect()
  }

  sendCommand(command: Command): number {
    // RpcClient.send drops messages silently when not open.
    if (!this.canSend()) throw new TransportError(`command sent on a ${this.state.kind} room connection whose link is down`)
    const seq = this.nextSeq++
    this.client.send('command', { seq, command })
    return seq
  }

  onView(listener: (frame: ViewFrame) => void): Unsubscribe {
    return this.views.add(listener)
  }

  onPrivate(listener: (state: PlayerPrivateState) => void): Unsubscribe {
    return this.privates.add(listener)
  }

  onEvents(listener: (events: readonly SimEvent[]) => void): Unsubscribe {
    return this.events.add(listener)
  }

  onLost(listener: (error: TransportError) => void): Unsubscribe {
    const state = this.state
    if (state.kind === 'lost') listener(state.error)
    return this.lost.add(listener)
  }

  dispose(): void {
    if (this.state.kind === 'disposed') return
    this.state = { kind: 'disposed' }
    this.stopped()
    this.client.disconnect()
    this.views.clear()
    this.privates.clear()
    this.events.clear()
    this.lost.clear()
    this.disposeChannels()
  }

  /** Whether a command can go out now: the socket is open (and, online, the seat's link is live). */
  protected canSend(): boolean {
    return this.client.isConnected
  }

  /** Whether the channel still runs (not lost, faulted or disposed). */
  protected get running(): boolean {
    return this.state.kind === 'open'
  }

  /** Commands continue after the last seq the room saw from this seat (an online rejoin). */
  protected resumeAfter(lastSeq: number): void {
    this.nextSeq = Math.max(this.nextSeq, lastSeq + 1)
  }

  /** The room is out of reach for good: the match ends in "connection lost". */
  protected lose(error: TransportError): void {
    if (this.state.kind !== 'open') throw new InvariantViolation(`room lost on a ${this.state.kind} connection`)
    this.state = { kind: 'lost', error }
    this.stopped()
    this.lost.emit(error)
  }

  /** The channel stopped for good (lost, faulted or disposed): subclasses stop their timers. */
  protected abstract stopped(): void

  /** Subclasses release their own listener lists. */
  protected abstract disposeChannels(): void

  /**
   * The room has stopped. Disconnecting rejects every pending call (a start in flight fails at once instead of
   * timing out); the fault is then raised like any other uncaught error.
   */
  protected readonly handleFault = (fault: RoomFault): void => {
    if (this.state.kind !== 'open') throw new InvariantViolation(`room fault on a ${this.state.kind} connection`)
    this.state = { kind: 'faulted' }
    this.stopped()
    this.client.disconnect()
    reportError(new RoomFaultError(fault))
  }

  /** The socket closed on its own: the room is lost (an online room first tries to rejoin, OnlineRpcRoom). */
  protected readonly handleDisconnect = (reason?: string): void => {
    // After dispose() or a fault the close is our own.
    if (this.state.kind !== 'open') return
    this.lose(new TransportError(`room connection lost: ${reason ?? 'no reason given'}`))
  }
}

/** RpcClient catches decode failures and listener exceptions and hands them here: report them as uncaught (fail loud). */
const reportRpcError = (error: Error): void => reportError(error)

class LocalRpcRoom extends RpcRoomChannel implements LocalRoomConnection {
  protected readonly client: RpcClient<TaxwarProtocol>
  readonly presence = null
  readonly link = null
  readonly pause: PauseControl = {
    pause: () => this.client.call('pause', undefined),
    resume: () => this.client.call('resume', undefined),
  }

  constructor(url: string, webSocketFactory: WebSocketFactory) {
    super()
    // A room is one war: reconnecting would reach a fresh room (for the local room, a new Worker).
    this.client = new RpcClient(taxwarProtocol, url, {
      codec: createProtocolCodec(),
      webSocketFactory,
      autoReconnect: false,
      onDisconnect: this.handleDisconnect,
      onError: reportRpcError,
    })
    this.client.on('view', this.views.emit)
    this.client.on('private', this.privates.emit)
    this.client.on('events', this.events.emit)
    this.client.on('fault', this.handleFault)
  }

  startSkirmish(setup: SkirmishSetup): Promise<MatchSetupView> {
    return this.client.call('startSkirmish', setup)
  }

  protected stopped(): void {
    // The local room runs no timers.
  }

  protected disposeChannels(): void {
    // The local room has no channels of its own.
  }
}

/** Opens one socket to an online room, telling `observer` its close code and marking its own error events. */
export type RoomSocketOpener = (url: string, observer: SocketObserver) => WebSocketAdapter

/** RpcClient's call timeout: the room answers join at once, so a join this late is a failed start. */
const JOIN_TIMEOUT_MS = 10_000
/**
 * A rejoin attempt (open, then join) that has not succeeded by then is given up and the next one starts. RpcClient's
 * open has no timeout of its own, and a socket to a black-holed network can take minutes to fail. An attempt started
 * at the end of the grace still gets its full time: a room that has just surrendered for the seat holds its result.
 */
const ATTEMPT_TIMEOUT_MS = 6_000
/**
 * A live link that hears nothing from the room for this long is dead: a sleeping laptop or a changed network can keep
 * a socket "open" for minutes. The room sends 20 view frames a second in a war, and presence every second before it.
 */
const SILENCE_LIMIT_MS = 5_000
const WATCHDOG_PERIOD_MS = 1_000
/** Waits before each rejoin attempt; the last one repeats until the reconnect grace runs out. */
const REJOIN_DELAYS_MS: readonly number[] = [0, 500, 1_000, 2_000, 3_000]
const MS_PER_SECOND = 1_000
/** The room surrenders for a seat away this long (content ONLINE_MATCH): past it, rejoining cannot save the war. */
const RECONNECT_GRACE_MS = ONLINE_MATCH.reconnectGraceSeconds * MS_PER_SECOND
const GRACE_EXPIRED = `Could not reach the war room for ${ONLINE_MATCH.reconnectGraceSeconds} s. The room surrenders for a seat that stays away that long.`
const ENDED_WHILE_AWAY = 'The war ended while you were disconnected.'
const SEAT_REFUSED = 'The war room no longer accepts this seat.'

/** Room closes that rejoining cannot undo, and what the player is told; every other close (a network drop above all) is retried. */
const FINAL_CLOSES: ReadonlyMap<number, string> = new Map<number, string>([
  [CloseCode.normal, ENDED_WHILE_AWAY],
  [CloseCode.roomClosed, ENDED_WHILE_AWAY],
  [CloseCode.replaced, 'This war was opened in another tab or window, which took over your seat.'],
  [CloseCode.claimRejected, SEAT_REFUSED],
  [CloseCode.notSeated, SEAT_REFUSED],
  [CloseCode.policyViolation, 'The war room closed the connection after a malformed message.'],
  [CloseCode.internalError, 'The war room failed.'],
])

/** What ending on `close` tells the player, or null when the close is worth a rejoin. */
function finalCloseError(close: SocketClose | null): TransportError | null {
  const copy = close === null ? undefined : FINAL_CLOSES.get(close.code)
  if (close === null || copy === undefined) return null
  return new TransportError(close.reason === '' ? copy : `${copy} (${close.reason})`)
}

/** One socket to the room, with how it closed. */
class RoomSocket {
  /** Recorded by the socket before RpcClient hears of the close. */
  close: SocketClose | null = null
  /** The seat joined on this socket: the room's messages on it are the seat's. */
  joined = false
  /** Given up by the channel (replaced, or the channel stopped): its callbacks no longer count. */
  abandoned = false
  readonly client: RpcClient<OnlineWarProtocol>

  constructor(url: string, open: RoomSocketOpener, onClosed: (socket: RoomSocket, reason?: string) => void) {
    const errors = new SocketErrorScope()
    const observer: SocketObserver = {
      closed: (close) => {
        this.close = close
      },
      errors,
    }
    // A reconnect needs a fresh claim-bearing socket and a rejoin (the channel's), never RpcClient's own auto-reconnect.
    this.client = new RpcClient(onlineWarProtocol, url, {
      codec: createProtocolCodec(),
      webSocketFactory: (socketUrl) => open(socketUrl, observer),
      autoReconnect: false,
      timeout: JOIN_TIMEOUT_MS,
      onDisconnect: (reason) => onClosed(this, reason),
      // A failed open or a broken link is the close's (and the rejoin's) to handle; every other error is a bug.
      onError: (error) => {
        if (!errors.active) reportRpcError(error)
      },
    })
  }

  /** Closes it unless already given up; its close callback then finds it abandoned. */
  abandon(): void {
    if (this.abandoned) return
    this.abandoned = true
    this.client.disconnect()
  }
}

type SeatLink =
  /** Before the first join: a loss is a failed start, not a drop. */
  | { readonly kind: 'joining' }
  | { readonly kind: 'live' }
  | {
      readonly kind: 'reconnecting'
      readonly giveUpAtMs: number
      attempts: number
      /** The wait before the next attempt, or the running attempt's deadline. */
      timer: ReturnType<typeof setTimeout> | null
    }

type Reconnecting = Extract<SeatLink, { kind: 'reconnecting' }>

class OnlineRpcRoom extends RpcRoomChannel implements OnlineRoomConnection {
  readonly pause = null
  readonly presence: PresenceFeed
  readonly link: LinkFeed
  private readonly presences = new Listeners<RoomPresence>()
  private readonly links = new Listeners<LinkState>()
  private readonly resyncs = new Listeners<void>()
  private latestPresence: RoomPresence | null = null
  private socket: RoomSocket
  private seatLink: SeatLink = { kind: 'joining' }
  /** The war the first join answered with; every rejoin must name the same one. */
  private war: MatchSetupView | null = null
  private lastHeardMs = 0
  private watchdog: ReturnType<typeof setInterval> | null = null

  constructor(
    private readonly url: string,
    private readonly openSocket: RoomSocketOpener,
  ) {
    super()
    this.socket = this.newSocket()
    this.presence = {
      onPresence: (listener) => {
        if (this.latestPresence !== null) listener(this.latestPresence)
        return this.presences.add(listener)
      },
    }
    this.link = {
      onLink: (listener) => {
        const state = this.linkState()
        if (state !== null) listener(state)
        return this.links.add(listener)
      },
      onResync: (listener) => this.resyncs.add(listener),
    }
  }

  protected get client(): RpcClient<OnlineWarProtocol> {
    return this.socket.client
  }

  async join(): Promise<OnlineJoin> {
    if (this.seatLink.kind !== 'joining') throw new InvariantViolation('an online room is joined once; the channel rejoins by itself')
    const socket = this.socket
    const join = await socket.client.call('join', undefined)
    this.war = join.setup
    this.resumeAfter(join.lastSeq)
    socket.joined = true
    this.goLive()
    return join
  }

  protected override canSend(): boolean {
    return this.seatLink.kind === 'live' && this.socket.client.isConnected
  }

  protected stopped(): void {
    if (this.watchdog !== null) clearInterval(this.watchdog)
    this.watchdog = null
    const link = this.seatLink
    if (link.kind === 'reconnecting') clearLinkTimer(link)
    this.socket.abandon()
  }

  protected disposeChannels(): void {
    this.presences.clear()
    this.links.clear()
    this.resyncs.clear()
  }

  private linkState(): LinkState | null {
    const link = this.seatLink
    if (link.kind === 'joining') return null
    return link.kind === 'live' ? { kind: 'live' } : { kind: 'reconnecting', giveUpAtMs: link.giveUpAtMs }
  }

  private emitLink(): void {
    const state = this.linkState()
    if (state !== null) this.links.emit(state)
  }

  /** A socket to the room, its messages wired to the channel. */
  private newSocket(): RoomSocket {
    const socket = new RoomSocket(this.url, this.openSocket, this.socketClosed)
    const client = socket.client
    client.on('view', (frame) => {
      if (this.heard(socket)) this.views.emit(frame)
    })
    client.on('private', (state) => {
      if (this.heard(socket)) this.privates.emit(state)
    })
    client.on('events', (events) => {
      if (this.heard(socket)) this.events.emit(events)
    })
    client.on('presence', (presence) => {
      if (!this.heard(socket)) return
      this.latestPresence = presence
      this.presences.emit(presence)
    })
    // A failing room tells every admitted socket, joined or not.
    client.on('fault', (fault) => {
      if (!socket.abandoned) this.handleFault(fault)
    })
    return socket
  }

  /** A room message on `socket`: false once the socket was given up. The room never speaks to a seat before its join. */
  private heard(socket: RoomSocket): boolean {
    if (socket.abandoned) return false
    if (!socket.joined) throw new InvariantViolation('the war room sent a message before the seat joined')
    this.lastHeardMs = Date.now()
    return true
  }

  private readonly socketClosed = (socket: RoomSocket, reason?: string): void => {
    if (socket.abandoned || !this.running) return
    const link = this.seatLink
    // A rejoin attempt's own failure is handled where it awaits the socket.
    if (link.kind === 'reconnecting') return
    if (link.kind === 'joining') {
      this.lose(new TransportError(`room connection lost: ${reason ?? 'no reason given'}`))
      return
    }
    socket.abandoned = true
    const final = finalCloseError(socket.close)
    if (final !== null) this.lose(final)
    else this.reconnect()
  }

  private readonly checkSilence = (): void => {
    if (this.seatLink.kind === 'live' && Date.now() - this.lastHeardMs > SILENCE_LIMIT_MS) this.reconnect()
  }

  private goLive(): void {
    this.seatLink = { kind: 'live' }
    this.lastHeardMs = Date.now()
    if (this.watchdog === null) this.watchdog = setInterval(this.checkSilence, WATCHDOG_PERIOD_MS)
    this.emitLink()
  }

  /** The live link dropped: commands stop, and the seat rejoins until the grace runs out. */
  private reconnect(): void {
    this.socket.abandon()
    this.seatLink = { kind: 'reconnecting', giveUpAtMs: Date.now() + RECONNECT_GRACE_MS, attempts: 0, timer: null }
    this.emitLink()
    this.scheduleRejoin()
  }

  /** The next attempt after its backoff wait, cut short so one still starts at the end of the grace; after it, give up. */
  private scheduleRejoin(): void {
    const link = this.seatLink
    if (link.kind !== 'reconnecting') throw new InvariantViolation(`rejoin scheduled on a ${link.kind} link`)
    const remaining = link.giveUpAtMs - Date.now()
    if (remaining <= 0) {
      this.lose(new TransportError(GRACE_EXPIRED))
      return
    }
    const delay = REJOIN_DELAYS_MS[Math.min(link.attempts, REJOIN_DELAYS_MS.length - 1)] as number
    link.timer = setTimeout(() => {
      link.timer = null
      void this.rejoin(link)
    }, Math.min(delay, remaining))
  }

  /**
   * One rejoin attempt: a new socket to the same room, then join, within ATTEMPT_TIMEOUT_MS. Every step re-checks that
   * the attempt is still the current one (the deadline, a loss or dispose() may have superseded it meanwhile).
   */
  private async rejoin(link: Reconnecting): Promise<void> {
    link.attempts += 1
    const socket = this.newSocket()
    this.socket = socket
    link.timer = setTimeout(() => {
      link.timer = null
      this.rejoinFailed(link, socket)
    }, ATTEMPT_TIMEOUT_MS)
    try {
      await socket.client.connect()
    } catch (error) {
      // RpcClient rejects a failed open with a plain Error.
      if (!(error instanceof Error)) throw error
      this.rejoinFailed(link, socket)
      return
    }
    if (!this.isCurrentAttempt(link, socket)) return
    let join: OnlineJoin
    try {
      join = await socket.client.call('join', undefined)
    } catch (error) {
      if (!(error instanceof RpcError)) throw error
      this.rejoinFailed(link, socket)
      return
    }
    if (!this.isCurrentAttempt(link, socket)) return
    const war = this.war
    if (war === null || join.setup.seed !== war.seed || join.setup.you !== war.you) throw new InvariantViolation('the room answered a rejoin with another war')
    clearLinkTimer(link)
    this.resumeAfter(join.lastSeq)
    socket.joined = true
    this.resyncs.emit(undefined)
    this.goLive()
  }

  private isCurrentAttempt(link: Reconnecting, socket: RoomSocket): boolean {
    return this.running && this.seatLink === link && this.socket === socket && !socket.abandoned
  }

  /** The attempt failed (or ran out of time): end on a final close, else wait and try again. */
  private rejoinFailed(link: Reconnecting, socket: RoomSocket): void {
    if (!this.isCurrentAttempt(link, socket)) return
    clearLinkTimer(link)
    socket.abandon()
    const final = finalCloseError(socket.close)
    if (final !== null) this.lose(final)
    else this.scheduleRejoin()
  }
}

function clearLinkTimer(link: Reconnecting): void {
  if (link.timer !== null) clearTimeout(link.timer)
  link.timer = null
}

/** Connects to the local room through its Worker socket factory; the promise resolves once the socket is open. */
export async function connectLocalRoom(url: string, webSocketFactory: WebSocketFactory): Promise<LocalRoomConnection> {
  return open(new LocalRpcRoom(url, webSocketFactory))
}

/** Connects to an online war room; the promise resolves once the socket is open (join next). */
export async function connectOnlineRoom(url: string, openSocket: RoomSocketOpener): Promise<OnlineRoomConnection> {
  return open(new OnlineRpcRoom(url, openSocket))
}

async function open<T extends RpcRoomChannel>(connection: T): Promise<T> {
  try {
    await connection.connect()
  } catch (error) {
    // A socket that never opened still owns its room (the local room's Worker).
    connection.dispose()
    throw error
  }
  return connection
}
